import React, { FC, useEffect, useMemo, useState } from "react";
import Form from "../../components/Form/Form";
import { FormFieldT } from "../../models/models";
import { WizardModeE, WizardTypeE } from "./models";
import { ValidatorsE } from "../../components/Form/yupValidations";
import { useSelectOptions } from "./helpers/useSelectOptions";
import WizardHeader from "./WizardHeader";
import { selectValue } from "../../utils/helpers/selectOptions";
import { mapCampaignDataForSubmitting } from "./helpers/mapCampaignDataForSubmitting";
import {
  useCreateProjectMutation,
  useGetProjectByIdQuery,
  useUpdateProjectShortMutation,
} from "../../api/projectsApi";
import { useNavigate } from "react-router-dom";
import usePopup from "../../utils/hooks/usePopup";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectCurrentUser } from "../../store/selectors/authSelectors";
import Loader from "../../components/Loader/Loader";
import { toast } from "react-toastify";
import { TOAST_DURATION } from "../../utils/helpers/constants";
import { MEDIA_COMPANY_NONE_ID } from "../../utils/constants";
import dayjs from "dayjs";
import CustomNameExtraContent from "./CustomNameExtraContent";
import { getMaxAllowedCampaignNameChars } from "./helpers/getMaxAllowedCampaignNameChars";
import * as yup from "yup";
import { mapCampaignDataForUpdate } from "./helpers/mapCampaignDataForUpdate";
import { CreateNewAdvertiserShortWithName } from "../CreateNewAdvertiserShort";
import { CreateNewBrandShortWithName } from "../CreateNewBrandShort";
import { CreateNewMediaCompanyShortWithName } from "../CreateNewMediaCompanyShort";
import { selectIsFormDirty } from "../../store/selectors/appSelectors";
import UnsavedChangesWarning from "../UnsavedChangesWarning";
import { isMobileOrTablet } from "../../utils/helpers/mediaQueries";
import { UserRoleE } from "../../models/enums";
import ExistingCampaignPrompt from "../ExistingCampaignPrompt";
import { buildExistingCampaignActions } from "./helpers/existingCampaignActions";
import calcAdvertisingAgencyId from "./helpers/calcAdvertisingAgencyId";
import { canCreateMediaCompany } from "./helpers/canCreateMediaCompany";
import {
  selectSelectedAdvertiser,
  selectSelectedBrand,
  selectSelectedMediaCompany,
  selectSelectedIndustry,
} from "../../store/selectors/deliveryWizardSelector";
import {
  setSelectedAdvertiser,
  setSelectedBrand,
  setSelectedMediaCompany,
  setSelectedIndustry,
} from "../../store/slices/deliveryWizardSlice";
import buildCampaignNameString from "./helpers/buildCampaignNameString";

import styles from "./styles.module.scss";
import useLastBrandAndIndustryIds from "./helpers/useLastBrandAndIndustryIds";
import { useTranslate } from "../../utils/hooks/useTranslate";

type CampaignWizardPropTypes = {
  mode: WizardModeE;
  campaignDeliveryId: number;
  campaignId?: number;
  type: WizardTypeE;
};

const CampaignWizard: FC<CampaignWizardPropTypes> = (props) => {
  const user = useAppSelector(selectCurrentUser);
  const isDirty = useAppSelector(selectIsFormDirty);
  const { createPopup, removePopup } = usePopup();
  const { mode, campaignDeliveryId, type } = props;
  const { data: defaultData, isFetching: isFetchingDefaultData } =
    useGetProjectByIdQuery(
      { id: campaignDeliveryId },
      { skip: !campaignDeliveryId }
    );
  const navigate = useNavigate();
  const { t } = useTranslate();
  const { removeAllPopups } = usePopup();
  const [createCampaign] = useCreateProjectMutation();
  // const [updateCampaign] = useUpdateProjectMutation();
  const [updateCampaignShort] = useUpdateProjectShortMutation();
  const [broadcastingDate, setBroadcastingDate] = useState(
    defaultData?.BroadcastingDate || ""
  );
  const [campaignName, setCampaignName] = useState("");
  const [tempName, setTempName] = useState("");
  const maxChars = useMemo(
    () => getMaxAllowedCampaignNameChars(campaignName, tempName, true),
    [campaignName, tempName]
  );
  const dispatch = useAppDispatch();

  const [newAdvertiserName, setNewAdvertiserName] = useState("");
  const [newBrandName, setNewBrandName] = useState("");
  const [newMediaCompanyName, setNewMediaCompanyName] = useState("");

  const selectedAdvertiser = useAppSelector(selectSelectedAdvertiser);
  const selectedBrand = useAppSelector(selectSelectedBrand);
  const selectedMediaCompany = useAppSelector(selectSelectedMediaCompany);
  const selectedIndustry = useAppSelector(selectSelectedIndustry);

  const {
    advertisersSO,
    brandsSO,
    industrySO,
    advertisingAgenciesSO,
    mediaCompaniesSO,
    isFetchingSelectOptions,
  } = useSelectOptions();

  // Media-company options: the always-present "Self Media Buy" sentinel (agency buys its own media;
  // the payload builders map it to null) followed by the agency's same-country media companies.
  const mediaCompanyOptions = useMemo(
    () => [
      { value: MEDIA_COMPANY_NONE_ID, label: t`Self Media Buy` },
      ...mediaCompaniesSO,
    ],
    [mediaCompaniesSO, t]
  );

  useLastBrandAndIndustryIds();

  useEffect(() => {
    return () => {
      dispatch(setSelectedAdvertiser(null));
      dispatch(setSelectedBrand(null));
      dispatch(setSelectedMediaCompany(null));
      dispatch(setSelectedIndustry(null));
    };
  }, []);

  useEffect(() => {
    if (defaultData) {
      if (!selectedAdvertiser) {
        dispatch(
          setSelectedAdvertiser(
            selectValue(advertisersSO, +defaultData.AdvertiserId)
          )
        );
      }
      if (!selectedBrand) {
        dispatch(setSelectedBrand(selectValue(brandsSO, +defaultData.BrandId)));
      }
      if (!selectedMediaCompany) {
        // Pre-select the stored media company; a null MediaCompanyId (every pre-existing campaign)
        // resolves to the "Self Media Buy" sentinel, preserving the current behavior.
        dispatch(
          setSelectedMediaCompany(
            selectValue(
              mediaCompanyOptions,
              defaultData?.MediaCompanyId ?? MEDIA_COMPANY_NONE_ID
            )
          )
        );
      }
    }
  }, [
    defaultData,
    advertisersSO,
    brandsSO,
    mediaCompanyOptions,
    selectedBrand,
    selectedAdvertiser,
    selectedMediaCompany,
    selectedIndustry,
  ]);

  // Seed the locally-held fields ONCE per loaded project. This deliberately depends on nothing but
  // `defaultData`: the effect above re-runs on every render (`mediaCompanyOptions` is rebuilt each
  // render because `t` is not memoized) and on every market-category pick (`selectedIndustry`), so
  // seeding from there kept resetting the broadcasting date and the custom name the user had just
  // entered — the edited date could never survive to submit.
  useEffect(() => {
    if (!defaultData) {
      return;
    }
    setBroadcastingDate(defaultData.BroadcastingDate);
    setCampaignName(
      defaultData.Name?.split("—")?.[0]?.trim() || defaultData.Name
    );
    setTempName(defaultData.Name?.split("—")?.[1]?.trim() || "");
  }, [defaultData]);

  useEffect(() => {
    const campaignNameString = buildCampaignNameString(
      selectedBrand?.shortName,
      selectedAdvertiser?.shortName,
      broadcastingDate
    );
    setCampaignName(campaignNameString);
  }, [
    selectedAdvertiser?.shortName,
    selectedBrand?.shortName,
    broadcastingDate,
    defaultData,
    newBrandName,
    newAdvertiserName,
  ]);

  useEffect(() => {
    // const popupOverlay = document.getElementById('popup-overlay');
    const popupCloseIcon = document.getElementById("popup-close-icon");

    // popupOverlay.addEventListener('click', overrideHandleClosePopup);
    popupCloseIcon.addEventListener("click", overrideHandleClosePopup);
    return () => {
      // popupOverlay.removeEventListener('click', overrideHandleClosePopup);
      popupCloseIcon.removeEventListener("click", overrideHandleClosePopup);
    };
  }, [isDirty]);

  const overrideHandleClosePopup = (e: MouseEvent) => {
    if (e.target === e.currentTarget && isDirty) {
      e.stopImmediatePropagation();
      createPopup(UnsavedChangesWarning);
    }
  };

  const createNew = (type: "brand" | "advertiser" | "mediaCompany") => {
    const popupData =
      type === "advertiser"
        ? CreateNewAdvertiserShortWithName(newAdvertiserName)
        : type === "brand"
        ? CreateNewBrandShortWithName(newBrandName)
        : CreateNewMediaCompanyShortWithName(newMediaCompanyName);
    createPopup(popupData);
  };

  // A media-company user may neither reassign the campaign's media company (field read-only) nor
  // create one — see canCreateMediaCompany for the rule and why delivery-service users keep it.
  const isMediaCompanyUser = user.role === UserRoleE.MediaCompany;
  const mayCreateMediaCompany = canCreateMediaCompany(user.role);

  const formFields: FormFieldT[] = useMemo(
    () =>
      isFetchingSelectOptions ||
      isFetchingDefaultData ||
      !advertisingAgenciesSO?.length
        ? []
        : [
            {
              label: "Advertiser",
              hintText:
                'If the relevant advertiser of the project is not listed, you can add it by clicking "Create new advertiser"',
              selectOptions: advertisersSO,
              controlledValue: selectedAdvertiser,
              onInput: (value) => setNewAdvertiserName(value),
              onChange: (option) => dispatch(setSelectedAdvertiser(option)),
              validation: selectedAdvertiser
                ? undefined
                : ValidatorsE.SELECT_REQUIRED,
              createNewOption: () => createNew("advertiser"),
              addAction: () => createNew("advertiser"),
            },
            {
              label: "Brand",
              hintText:
                'If the brand name of the project is not listed, you can add it by clicking "Create a new brand". In addition to the name, add its AD-ID. AD-ID is a unique brand identifier which consists of 4-6 letters in English, allowing identification of the brand in the system. This is also the ID that will appear in the file name throughout the project.',
              selectOptions: brandsSO,
              controlledValue: selectedBrand,
              onInput: (value) => setNewBrandName(value),
              onChange: (option) => dispatch(setSelectedBrand(option)),
              validation: selectedBrand
                ? undefined
                : ValidatorsE.SELECT_REQUIRED,
              createNewOption: () => createNew("brand"),
              addAction: () => createNew("brand"),
            },
            {
              label: "Broadcasting date",
              hintText: "Please select the intended broadcast date",
              type: "date",
              onChange: (value) => setBroadcastingDate(value),
              defaultValue:
                defaultData?.BroadcastingDate || broadcastingDate
                  ? dayjs(
                      defaultData?.BroadcastingDate || broadcastingDate
                    ).format("YYYY-MM-DD")
                  : undefined,
              validation: broadcastingDate
                ? undefined
                : ValidatorsE.REQUIRED_STRING,
              style: !isMobileOrTablet()
                ? {
                    // gridColumn: '1 / 3',
                    // width: 'calc(50% - 38px)',
                    marginBottom: 30,
                  }
                : undefined,
              props: {
                min:
                  mode === WizardModeE.Edit &&
                  user.role !== UserRoleE.GlobalAdmin &&
                  user.role !== UserRoleE.GlobalAdminAndAdminContentManager &&
                  (defaultData?.BroadcastingDate || broadcastingDate)
                    ? dayjs(defaultData?.BroadcastingDate || broadcastingDate)
                        .toISOString()
                        .split("T")[0]
                    : mode === WizardModeE.Create &&
                      user.role === UserRoleE.AdvertisingAgency
                    ? dayjs().toISOString().split("T")[0]
                    : undefined,
              },
            },
            {
              label: "Name",
              defaultValue: tempName,
              onChange: (value) => setTempName(value),
              validation: yup
                .string()
                .matches(/^[^,;]*$/, t`The use of a comma (,) or a semicolon (;) chars is not allowed.`)
                .max(+maxChars, () => t`${maxChars} characters maximum`),
              props: {
                id: "campaignName",
                maxlength: +maxChars,
              },
              style: !isMobileOrTablet()
                ? {
                    // gridColumn: '1 / 3',
                    // width: 'calc(50% - 38px)',
                    // marginBottom: 50,
                  }
                : undefined,
              extraContent: () => (
                <CustomNameExtraContent
                  defaultName={defaultData?.Name}
                  campaignName={campaignName}
                  tempName={tempName}
                  mode={mode}
                />
              ),
            },
            {
              label: "Market category",
              hintText: "Select market category",
              selectOptions: industrySO,
              controlledValue: selectedIndustry,
              onChange: (option) => dispatch(setSelectedIndustry(option)),
              validation: selectedIndustry
                ? undefined
                : ValidatorsE.SELECT_REQUIRED,
              defaultValue: selectValue(industrySO, defaultData?.IndustryId),
            },
            {
              label: "Advertising agency",
              selectOptions: advertisingAgenciesSO,
              hintText:
                "If you are representing an advertising agency other than yourself in this campaign, you can select the desired agency from the list.If the name of the desired advertising agency does not appear in this list, please contact us for support.",
              validation: ValidatorsE.SELECT_REQUIRED,
              // A media-company user edits every field except the advertising agency, which is shown
              // read-only (the backend also guards this value against media-company callers).
              props: { disabled: user.role === UserRoleE.MediaCompany },
              defaultValue:
                advertisingAgenciesSO.find(
                  (item: any) =>
                    item.label === defaultData?.AdvertisingAgencyName
                ) ||
                selectValue(
                  advertisingAgenciesSO,
                  defaultData?.AdvertisingAgencyId || user.id
                ),
            },
            {
              label: "Media Company",
              hintText:
                "Create a Media Company. If there isn't one for this specific campaign, select 'Self Media Buy'.",
              selectOptions: mediaCompanyOptions,
              controlledValue: selectedMediaCompany,
              onInput: (value) => setNewMediaCompanyName(value),
              onChange: (option) => dispatch(setSelectedMediaCompany(option)),
              validation: selectedMediaCompany
                ? undefined
                : ValidatorsE.SELECT_REQUIRED,
              // Both the in-dropdown "create new" entry and the "+" icon next to the label are
              // dropped when the user may not create a media company. Without addAction the label
              // falls back to the plain info icon, so the hint text stays reachable.
              createNewOption: mayCreateMediaCompany
                ? () => createNew("mediaCompany")
                : undefined,
              addAction: mayCreateMediaCompany
                ? () => createNew("mediaCompany")
                : undefined,
              // A media-company user may correct the campaign's advertiser/brand/date/name/category
              // but not reassign the campaign to a different media company — so this field is also
              // read-only for them (the backend guards the value as well). In Edit mode the value is
              // pre-selected, so a disabled+prefilled field still validates.
              props: { disabled: isMediaCompanyUser },
            },
          ],
    [
      isFetchingSelectOptions,
      isFetchingDefaultData,
      advertisingAgenciesSO,
      advertisersSO,
      selectedAdvertiser,
      brandsSO,
      selectedBrand,
      selectedMediaCompany,
      defaultData?.BroadcastingDate,
      defaultData?.IndustryId,
      defaultData?.AdvertisingAgencyId,
      defaultData?.Name,
      defaultData?.AdvertisingAgencyName,
      defaultData?.MediaCompanyId,
      mediaCompanyOptions,
      broadcastingDate,
      mode,
      user.role,
      isMediaCompanyUser,
      mayCreateMediaCompany,
      user.id,
      tempName,
      maxChars,
      industrySO,
      selectedIndustry,
      dispatch,
      createNew,
      campaignName,
    ]
  );

  const submit = async (data) => {
    if (mode === WizardModeE.Create || mode === WizardModeE.Clone) {
      const body = mapCampaignDataForSubmitting({
        ...data,
        advertiser: selectedAdvertiser,
        brand: selectedBrand,
        mediaCompany: selectedMediaCompany,
        marketCategory: selectedIndustry,
        broadcastingDate,
        campaignRetry: data.campaignRetry,
      });

      const res: any = await createCampaign({
        ...body,
        IsCampaignCreation: true,
        AdvertisingAgencyId: calcAdvertisingAgencyId(user.role, +user.id),
        AdvertisingAgencyName: data.advertisingAgency.label,
      });
      if (Array.isArray(res?.error?.data)) {
        // spec-47: 302 Found — campaigns for this advertiser + brand from this client with a broadcast date in
        // the last 30 days, newest-created first. Using one navigates to it; only a confirmed "separate
        // campaign" re-submits.
        createPopup(
          ExistingCampaignPrompt({
            campaigns: res.error.data,
            ...buildExistingCampaignActions({
              navigate,
              removeAllPopups,
              resubmitAsNew: () => submit({ ...data, campaignRetry: true, addToExist: 0 }),
            }),
          })
        );
      }
      const campaignId = res?.data?.Detailed?.Campaign?.Id;
      if (campaignId) {
        toast.success("Campaign created successfully", {
          autoClose: TOAST_DURATION,
        });
        navigate(`/campaigns/${campaignId}`);
        removeAllPopups();
      }
    } else if (mode === WizardModeE.Edit) {
      const body: any = mapCampaignDataForUpdate(
        {
          ...data,
          advertiser: selectedAdvertiser,
          brand: selectedBrand,
          mediaCompany: selectedMediaCompany,
          // Single source of truth for the date, like the create branch above. It also feeds the
          // regenerated campaign name, so it must be the edited value and not a leftover default.
          // (The payload used to carry a stray lower-case `broadcastingDate` alongside
          // `BroadcastingDate`; Json.NET binds case-insensitively, so the stale one won.)
          broadcastingDate: data.broadcastingDate || broadcastingDate,
        },
        defaultData
      );

      const res: any = await updateCampaignShort({
        ...body,
        PostProductionCompanyId: defaultData.PostProductionCompanyId,
        AdvertisingAgencyId: calcAdvertisingAgencyId(user.role, +user.id),
        AdvertisingAgencyName: data.advertisingAgency.label,
      });

      const campaignId = res?.data?.Detailed?.Campaign?.Id;

      if (campaignId) {
        toast.success("Campaign updated successfully", {
          autoClose: TOAST_DURATION,
        });
        navigate(`/campaigns/${campaignId}`);
        removeAllPopups();
      }
    }
  };

  return (
    <div>
      <WizardHeader mode={mode} type={type} />
      {!formFields.length ? (
        <Loader />
      ) : (
        <Form
          fields={formFields}
          formID="create-campaign"
          onSubmit={submit}
          columns={2}
          handleDirty
          preventSubmitWithEnter
        />
      )}
    </div>
  );
};

export default CampaignWizard;
